import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Clock, 
  Euro,
  Percent,
  Save,
  TrendingDown,
  Calculator,
  Info,
  RotateCcw,
  Package,
  Server,
  Wrench,
  Loader2,
  Plus,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

// Default progressive rates as specified by user
const DEFAULT_RATE_TIERS = [
  { min_hours: 0, max_hours: 5, rate: 120, label: '0-5h' },
  { min_hours: 5, max_hours: 10, rate: 115, label: '5-10h' },
  { min_hours: 10, max_hours: 15, rate: 110, label: '10-15h' },
  { min_hours: 15, max_hours: 20, rate: 105, label: '15-20h' },
  { min_hours: 20, max_hours: 30, rate: 100, label: '20-30h' },
  { min_hours: 30, max_hours: 40, rate: 95, label: '30-40h' },
  { min_hours: 40, max_hours: 50, rate: 90, label: '40-50h' },
  { min_hours: 50, max_hours: 60, rate: 85, label: '50-60h' },
  { min_hours: 60, max_hours: 80, rate: 80, label: '60-80h' },
  { min_hours: 80, max_hours: 100, rate: 75, label: '80-100h' },
  { min_hours: 100, max_hours: 150, rate: 70, label: '100-150h' },
  { min_hours: 150, max_hours: 200, rate: 65, label: '150-200h' },
  { min_hours: 200, max_hours: null, rate: 60, label: '200h+' },
];

// Default hosting plans
const DEFAULT_HOSTING_PLANS = [
  { id: 'starter', name: 'Starter', price: 15, storage: '5 Go', domains: 1, ssl: true, backup: 'Hebdo' },
  { id: 'pro', name: 'Pro', price: 29, storage: '20 Go', domains: 3, ssl: true, backup: 'Quotidien' },
  { id: 'business', name: 'Business', price: 49, storage: '50 Go', domains: 10, ssl: true, backup: 'Temps réel' },
];

// Default maintenance plans
const DEFAULT_MAINTENANCE_PLANS = [
  { id: 'basic', name: 'Essentiel', price: 99, hours: 1, includes: ['Mises à jour', 'Monitoring'], response: '48h' },
  { id: 'standard', name: 'Standard', price: 199, hours: 3, includes: ['Mises à jour', 'Monitoring', 'Corrections', 'Support prioritaire'], response: '24h' },
  { id: 'premium', name: 'Premium', price: 399, hours: 6, includes: ['Tout Standard', 'Évolutions mineures', 'Rapport mensuel'], response: '4h' },
];

const HourlyRatesPage = () => {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState('rates');
  const [rateTiers, setRateTiers] = useState(DEFAULT_RATE_TIERS);
  const [hostingPlans, setHostingPlans] = useState(DEFAULT_HOSTING_PLANS);
  const [maintenancePlans, setMaintenancePlans] = useState(DEFAULT_MAINTENANCE_PLANS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [simulationHours, setSimulationHours] = useState('');
  const [simulationResult, setSimulationResult] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [ratesRes, pricingRes] = await Promise.all([
        api.get('/settings/hourly-rates').catch(() => ({ data: {} })),
        api.get('/settings/pricing').catch(() => ({ data: {} }))
      ]);
      
      if (ratesRes.data?.tiers && ratesRes.data.tiers.length > 0) {
        setRateTiers(ratesRes.data.tiers);
      }
      if (pricingRes.data?.hosting_plans) {
        setHostingPlans(pricingRes.data.hosting_plans);
      }
      if (pricingRes.data?.maintenance_plans) {
        setMaintenancePlans(pricingRes.data.maintenance_plans);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRateChange = (index, value) => {
    const newTiers = [...rateTiers];
    newTiers[index].rate = parseFloat(value) || 0;
    setRateTiers(newTiers);
  };

  const handleSaveRates = async () => {
    setSaving(true);
    try {
      await api.post('/settings/hourly-rates', { tiers: rateTiers });
      toast.success('Taux horaires enregistrés !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePlans = async () => {
    setSaving(true);
    try {
      await api.put('/settings/pricing', { 
        hosting_plans: hostingPlans,
        maintenance_plans: maintenancePlans 
      });
      toast.success('Plans enregistrés !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setRateTiers(DEFAULT_RATE_TIERS);
    toast.info('Taux réinitialisés aux valeurs par défaut');
  };

  // Calculate price for given hours using progressive tiers
  const calculatePrice = (hours) => {
    let totalPrice = 0;
    let remainingHours = hours;
    let breakdown = [];

    for (let i = 0; i < rateTiers.length && remainingHours > 0; i++) {
      const tier = rateTiers[i];
      const tierMin = tier.min_hours;
      const tierMax = tier.max_hours || Infinity;
      const tierRange = tierMax - tierMin;
      const hoursInTier = Math.min(remainingHours, tierRange);
      
      if (hoursInTier > 0) {
        const tierCost = hoursInTier * tier.rate;
        totalPrice += tierCost;
        breakdown.push({
          hours: hoursInTier,
          rate: tier.rate,
          cost: tierCost,
          label: tier.label
        });
        remainingHours -= hoursInTier;
      }
    }

    const basePrice = hours * rateTiers[0].rate;
    const savings = basePrice - totalPrice;
    const avgRate = hours > 0 ? totalPrice / hours : 0;

    return { totalPrice, breakdown, savings, avgRate, basePrice };
  };

  const handleSimulate = () => {
    const hours = parseFloat(simulationHours);
    if (hours > 0) {
      setSimulationResult(calculatePrice(hours));
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(value);
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="hourly-rates-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
              Tarification
            </h1>
            <p className="text-slate-500 mt-1">
              Gérez vos taux horaires, plans d'hébergement et de maintenance
            </p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3 w-full max-w-md">
            <TabsTrigger value="rates" className="flex items-center gap-2">
              <TrendingDown className="w-4 h-4" />
              Taux Horaires
            </TabsTrigger>
            <TabsTrigger value="hosting" className="flex items-center gap-2">
              <Server className="w-4 h-4" />
              Hébergement
            </TabsTrigger>
            <TabsTrigger value="maintenance" className="flex items-center gap-2">
              <Wrench className="w-4 h-4" />
              Maintenance
            </TabsTrigger>
          </TabsList>

          {/* TAUX HORAIRES TAB */}
          <TabsContent value="rates" className="space-y-6 mt-6">
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleReset}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Réinitialiser
              </Button>
              <Button 
                onClick={handleSaveRates} 
                disabled={saving}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Rate Tiers Card */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingDown className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                    Grille Tarifaire Dégressive
                  </CardTitle>
                  <CardDescription>
                    Plus le client achète d'heures, plus le taux horaire diminue
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {rateTiers.map((tier, index) => (
                      <div 
                        key={index}
                        className={cn(
                          "p-3 rounded-xl border-2 transition-all",
                          index === 0 
                            ? "border-amber-300 bg-amber-50" 
                            : index === rateTiers.length - 1
                              ? "border-emerald-300 bg-emerald-50"
                              : "border-slate-200 bg-slate-50"
                        )}
                      >
                        <div className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {tier.label}
                        </div>
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            value={tier.rate}
                            onChange={(e) => handleRateChange(index, e.target.value)}
                            className="w-20 h-8 text-center font-bold text-lg"
                            min="0"
                            step="1"
                          />
                          <span className="text-sm text-slate-600">€/h</span>
                        </div>
                        {index > 0 && (
                          <div className="text-xs text-emerald-600 mt-1">
                            -{Math.round((1 - tier.rate / rateTiers[0].rate) * 100)}%
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 p-4 rounded-xl bg-blue-50 border border-blue-200">
                    <div className="flex gap-3">
                      <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">Comment ça fonctionne ?</p>
                        <p>Le tarif est calculé de manière progressive : les premières heures sont facturées au taux plein, puis les heures suivantes bénéficient automatiquement des remises par tranche. Le client voit ses économies en temps réel.</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Simulator Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                    Simulateur
                  </CardTitle>
                  <CardDescription>
                    Calculez le coût pour un nombre d'heures
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nombre d'heures</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        value={simulationHours}
                        onChange={(e) => setSimulationHours(e.target.value)}
                        placeholder="Ex: 25"
                        className="flex-1"
                        min="0"
                        step="0.5"
                      />
                      <Button onClick={handleSimulate} variant="outline">
                        Calculer
                      </Button>
                    </div>
                  </div>

                  {simulationResult && (
                    <div className="space-y-3 pt-3 border-t">
                      <div className="p-4 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 text-white">
                        <div className="text-sm opacity-80">Coût total</div>
                        <div className="text-2xl font-bold">
                          {formatCurrency(simulationResult.totalPrice)}
                        </div>
                        <div className="text-xs opacity-80 mt-1">
                          Taux moyen : {formatCurrency(simulationResult.avgRate)}/h
                        </div>
                      </div>

                      {simulationResult.savings > 0 && (
                        <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                          <div className="flex items-center gap-2 text-emerald-700">
                            <Percent className="w-4 h-4" />
                            <span className="font-medium">
                              Économie : {formatCurrency(simulationResult.savings)}
                            </span>
                          </div>
                          <div className="text-xs text-emerald-600 mt-1">
                            vs. tarif plein ({formatCurrency(simulationResult.basePrice)})
                          </div>
                        </div>
                      )}

                      <div className="space-y-2">
                        <div className="text-sm font-medium text-slate-600">Détail par tranche :</div>
                        {simulationResult.breakdown.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm p-2 bg-slate-50 rounded-lg">
                            <span className="text-slate-600">
                              {item.hours}h × {formatCurrency(item.rate)}
                            </span>
                            <span className="font-medium">{formatCurrency(item.cost)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="pt-3 border-t">
                    <div className="text-xs text-slate-500 mb-2">Exemples rapides :</div>
                    <div className="flex flex-wrap gap-2">
                      {[10, 25, 50, 100].map((h) => (
                        <Button
                          key={h}
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          onClick={() => {
                            setSimulationHours(h.toString());
                            setSimulationResult(calculatePrice(h));
                          }}
                        >
                          {h}h
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Summary Table */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Euro className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                  Récapitulatif des Tarifs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3 font-medium text-slate-600">Tranche</th>
                        <th className="text-right py-2 px-3 font-medium text-slate-600">Taux HT</th>
                        <th className="text-right py-2 px-3 font-medium text-slate-600">Réduction</th>
                        <th className="text-right py-2 px-3 font-medium text-slate-600">Exemple 10h</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rateTiers.map((tier, index) => {
                        const discount = index > 0 
                          ? Math.round((1 - tier.rate / rateTiers[0].rate) * 100)
                          : 0;
                        return (
                          <tr key={index} className="border-b last:border-b-0 hover:bg-slate-50">
                            <td className="py-2 px-3">
                              <Badge variant="outline" className="font-normal">
                                {tier.label}
                              </Badge>
                            </td>
                            <td className="text-right py-2 px-3 font-medium">
                              {formatCurrency(tier.rate)}/h
                            </td>
                            <td className="text-right py-2 px-3">
                              {discount > 0 ? (
                                <span className="text-emerald-600">-{discount}%</span>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            <td className="text-right py-2 px-3 text-slate-600">
                              {formatCurrency(tier.rate * 10)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* HÉBERGEMENT TAB */}
          <TabsContent value="hosting" className="space-y-6 mt-6">
            <div className="flex justify-end">
              <Button 
                onClick={handleSavePlans} 
                disabled={saving}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {hostingPlans.map((plan, index) => (
                <Card key={plan.id} className={cn(
                  "relative overflow-hidden",
                  index === 1 && "ring-2 ring-indigo-500"
                )}>
                  {index === 1 && (
                    <div className="absolute top-0 right-0 bg-indigo-500 text-white text-xs px-3 py-1 rounded-bl-lg">
                      Populaire
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Server className="w-5 h-5" />
                      {plan.name}
                    </CardTitle>
                    <div className="flex items-baseline gap-1">
                      <Input
                        type="number"
                        value={plan.price}
                        onChange={(e) => {
                          const newPlans = [...hostingPlans];
                          newPlans[index].price = parseFloat(e.target.value) || 0;
                          setHostingPlans(newPlans);
                        }}
                        className="w-20 text-2xl font-bold h-10"
                      />
                      <span className="text-slate-500">€/mois</span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Stockage</span>
                      <Input
                        value={plan.storage}
                        onChange={(e) => {
                          const newPlans = [...hostingPlans];
                          newPlans[index].storage = e.target.value;
                          setHostingPlans(newPlans);
                        }}
                        className="w-24 h-7 text-right text-sm"
                      />
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Domaines</span>
                      <Input
                        type="number"
                        value={plan.domains}
                        onChange={(e) => {
                          const newPlans = [...hostingPlans];
                          newPlans[index].domains = parseInt(e.target.value) || 1;
                          setHostingPlans(newPlans);
                        }}
                        className="w-24 h-7 text-right text-sm"
                      />
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Backup</span>
                      <Input
                        value={plan.backup}
                        onChange={(e) => {
                          const newPlans = [...hostingPlans];
                          newPlans[index].backup = e.target.value;
                          setHostingPlans(newPlans);
                        }}
                        className="w-24 h-7 text-right text-sm"
                      />
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">SSL inclus</span>
                      <Switch
                        checked={plan.ssl}
                        onCheckedChange={(checked) => {
                          const newPlans = [...hostingPlans];
                          newPlans[index].ssl = checked;
                          setHostingPlans(newPlans);
                        }}
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* MAINTENANCE TAB */}
          <TabsContent value="maintenance" className="space-y-6 mt-6">
            <div className="flex justify-end">
              <Button 
                onClick={handleSavePlans} 
                disabled={saving}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {maintenancePlans.map((plan, index) => (
                <Card key={plan.id} className={cn(
                  "relative overflow-hidden",
                  index === 1 && "ring-2 ring-emerald-500"
                )}>
                  {index === 1 && (
                    <div className="absolute top-0 right-0 bg-emerald-500 text-white text-xs px-3 py-1 rounded-bl-lg">
                      Recommandé
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Wrench className="w-5 h-5" />
                      {plan.name}
                    </CardTitle>
                    <div className="flex items-baseline gap-1">
                      <Input
                        type="number"
                        value={plan.price}
                        onChange={(e) => {
                          const newPlans = [...maintenancePlans];
                          newPlans[index].price = parseFloat(e.target.value) || 0;
                          setMaintenancePlans(newPlans);
                        }}
                        className="w-20 text-2xl font-bold h-10"
                      />
                      <span className="text-slate-500">€/mois</span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Heures incluses</span>
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          value={plan.hours}
                          onChange={(e) => {
                            const newPlans = [...maintenancePlans];
                            newPlans[index].hours = parseInt(e.target.value) || 0;
                            setMaintenancePlans(newPlans);
                          }}
                          className="w-16 h-7 text-right text-sm"
                        />
                        <span className="text-slate-500">h</span>
                      </div>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Temps de réponse</span>
                      <Input
                        value={plan.response}
                        onChange={(e) => {
                          const newPlans = [...maintenancePlans];
                          newPlans[index].response = e.target.value;
                          setMaintenancePlans(newPlans);
                        }}
                        className="w-20 h-7 text-right text-sm"
                      />
                    </div>
                    <div className="border-t pt-3 mt-3">
                      <div className="text-xs text-slate-500 mb-2">Inclus :</div>
                      <div className="space-y-1">
                        {plan.includes?.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};

export default HourlyRatesPage;
